import { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Input } from '@artstorefronts/ui';
import { MemberPicker, memberLabel, type PickedMember } from './MemberPicker';

const ADMIN_BOUNTY_BLOCKS = gql`
  query AdminBountyBlocks($id: ID!) {
    adminBountyBlocks(id: $id) {
      userId
      firstName
      lastName
      email
      reason
      blockedAt
    }
  }
`;

const ADMIN_BLOCK_FROM_BOUNTY = gql`
  mutation AdminBlockFromBounty($id: ID!, $userId: ID!, $reason: String) {
    adminBlockFromBounty(id: $id, userId: $userId, reason: $reason)
  }
`;

const ADMIN_UNBLOCK_FROM_BOUNTY = gql`
  mutation AdminUnblockFromBounty($id: ID!, $userId: ID!) {
    adminUnblockFromBounty(id: $id, userId: $userId)
  }
`;

type BlockedMember = {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  reason: string | null;
  blockedAt: string;
};

function blockedLabel(member: BlockedMember): string {
  if (!member.email) return `Member ${member.userId}`;
  return memberLabel({
    asfId: member.userId,
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email,
  });
}

/**
 * Members excluded from this bounty by hand — the opposite of an invite. An excluded member
 * stops seeing it and stops being paid for it; points already paid stay paid.
 */
export function BountyExclusions({ bountyId }: { bountyId: string }) {
  const { data, refetch } = useQuery<{ adminBountyBlocks: BlockedMember[] }>(ADMIN_BOUNTY_BLOCKS, {
    variables: { id: bountyId },
    fetchPolicy: 'cache-and-network',
  });
  const [block, { loading: blocking }] = useMutation(ADMIN_BLOCK_FROM_BOUNTY);
  const [unblock] = useMutation(ADMIN_UNBLOCK_FROM_BOUNTY);
  const [picked, setPicked] = useState<PickedMember | null>(null);
  const [reason, setReason] = useState('');
  // Bumped after each exclusion so the picker remounts with an empty search, not the last one.
  const [pickerKey, setPickerKey] = useState(0);
  const blocked = data?.adminBountyBlocks ?? [];

  const handleBlock = async () => {
    if (!picked) return;
    try {
      const result = await block({ variables: { id: bountyId, userId: picked.asfId, reason: reason.trim() || null } });
      toast.success(result.data.adminBlockFromBounty ? 'Excluded.' : 'Already excluded — reason updated.');
      setPicked(null);
      setReason('');
      setPickerKey((key) => key + 1);
      await refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to exclude');
    }
  };

  const handleUnblock = async (member: BlockedMember) => {
    try {
      await unblock({ variables: { id: bountyId, userId: member.userId } });
      toast.success('Exclusion lifted.');
      await refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to lift the exclusion');
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4" data-testid="bounty-exclusions-panel">
      <div>
        <p className="text-sm font-semibold text-gray-900">Excluded members</p>
        <p className="text-xs text-gray-500">
          An excluded member stops seeing this bounty and stops being paid for it. Points already paid stay paid.
        </p>
      </div>
      <MemberPicker key={pickerKey} picked={picked} onPick={setPicked} testId="bounty-exclude-search" />
      <Input
        placeholder="Why (optional), e.g. spam posts"
        value={reason}
        maxLength={500}
        onChange={(event) => setReason(event.target.value)}
        data-testid="bounty-exclude-reason"
      />
      <Button size="sm" variant="destructive" disabled={!picked || blocking} onClick={() => void handleBlock()}>
        Exclude
      </Button>
      {blocked.length > 0 ? (
        <ul className="divide-y border-t pt-1" data-testid="bounty-excluded-list">
          {blocked.map((member) => (
            <li key={member.userId} className="flex items-start justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="break-words text-sm text-gray-900">{blockedLabel(member)}</p>
                <p className="text-xs text-gray-500">
                  {new Date(member.blockedAt).toLocaleDateString()}
                  {member.reason ? ` · ${member.reason}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label={`Lift the exclusion for ${blockedLabel(member)}`}
                onClick={() => void handleUnblock(member)}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
